import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Statistic, Timeline, List, Typography, Spin, Progress, Empty } from 'antd';
import { ProjectOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import MySchedule from '../../components/ProjectBoard/MySchedule/MySchedule';
import OverviewFilters, { OVERVIEW_FILTER_KEYS } from '../../components/ProjectBoard/OverviewFilters';
import PriorityBreakdown from '../../components/ProjectBoard/PriorityBreakdown';

const { Title } = Typography;

const subStyle = { marginTop: 8, fontSize: 12, color: '#59636e' };
const linkStyle = { background: 'none', border: 0, padding: 0, color: '#bc4c00', cursor: 'pointer', textDecoration: 'underline dotted', font: 'inherit' };

// Task count with its complete / incomplete split, a progress bar, and the
// number of incomplete tasks that still have no due date.
const TaskCountCard = ({ title, icon, total = 0, completed = 0, incomplete = 0, noDue = 0, color, onNoDueClick, byPriority, activePriority, onPriorityClick }) => {
  const pct = total ? Math.round((completed / total) * 100) : 0;
  return (
    <Card style={{ height: '100%' }}>
      <Statistic title={title} value={total || 0} prefix={icon} valueStyle={color ? { color } : undefined} />
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13 }}>
        <span style={{ color: '#1a7f37' }}>{completed || 0} complete</span>
        <span style={{ color: '#bc4c00' }}>{incomplete || 0} incomplete</span>
      </div>
      <Progress percent={pct} size="small" strokeColor="#1a7f37" style={{ margin: '4px 0 0' }} />
      <div style={{ ...subStyle, marginTop: 4 }}>
        {noDue ? (
          <button type="button" style={linkStyle} onClick={onNoDueClick} title="Show only incomplete tasks with no due date">
            {noDue} incomplete with no due date
          </button>
        ) : 'Every incomplete task has a due date'}
      </div>
      <div style={{ ...subStyle, marginTop: 10, marginBottom: 4 }}>By priority</div>
      <PriorityBreakdown counts={byPriority} active={activePriority} onSelect={onPriorityClick} compact />
    </Card>
  );
};

const Overview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const requestId = useRef(0);

  // Filters live in the URL so they survive reloads and can be shared.
  const filtersKey = OVERVIEW_FILTER_KEYS.map((k) => searchParams.get(k) || '').join('|');
  const filters = useMemo(() => {
    const f = {};
    OVERVIEW_FILTER_KEYS.forEach((k) => { const v = searchParams.get(k); if (v) f[k] = v; });
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const setFilters = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    OVERVIEW_FILTER_KEYS.forEach((k) => params.delete(k));
    Object.entries(next).forEach(([k, v]) => { if (v) params.set(k, v); });
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchDashboardData = useCallback(async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    try {
      const [statsRes, activityRes, tasksRes] = await Promise.all([
        axios.get('/project_board/dashboard/stats/', { params: filters }),
        axios.get('/project_board/dashboard/recent_activity/', { params: filters }),
        axios.get('/project_board/dashboard/my_tasks/', { params: filters }),
      ]);
      if (id !== requestId.current) return; // a newer filter change already won
      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setMyTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      if (id === requestId.current) {
        setRefreshing(false);
        setInitialLoading(false);
      }
    }
  }, [filters]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  const showNoDue = () => setFilters({ ...filters, completion: 'open', due: 'none' });
  const setPriority = (p) => setFilters({ ...filters, priority: p || undefined });

  if (initialLoading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Project Board Overview</Title>

      <OverviewFilters value={filters} onChange={setFilters} onRefresh={fetchDashboardData} refreshing={refreshing} />

      <Spin spinning={refreshing} delay={200}>
        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ height: '100%' }}>
              <Statistic
                title="My Projects"
                value={stats.my_projects || 0}
                prefix={<ProjectOutlined />}
              />
              {stats.total_projects !== stats.my_projects && (
                <div style={subStyle}>{stats.total_projects || 0} projects visible to you</div>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ height: '100%' }}>
              <Statistic
                title="Active Projects"
                value={stats.active_projects || 0}
                valueStyle={{ color: '#3f8600' }}
              />
              <div style={subStyle}>Projects with status "In Progress"</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <TaskCountCard
              title="Total Tasks"
              icon={<ClockCircleOutlined />}
              total={stats.total_tasks}
              completed={stats.completed_tasks}
              incomplete={stats.incomplete_tasks}
              noDue={stats.incomplete_no_due_tasks}
              onNoDueClick={showNoDue}
              byPriority={stats.tasks_by_priority}
              activePriority={filters.priority}
              onPriorityClick={setPriority}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <TaskCountCard
              title="My Tasks"
              icon={<UserOutlined />}
              total={stats.my_tasks}
              completed={stats.my_tasks_completed}
              incomplete={stats.my_tasks_incomplete}
              noDue={stats.my_tasks_incomplete_no_due}
              onNoDueClick={showNoDue}
              byPriority={stats.my_tasks_by_priority}
              activePriority={filters.priority}
              onPriorityClick={setPriority}
              color="#1890ff"
            />
          </Col>
        </Row>
      </Spin>

      {/* Calendar / timeline of my assigned tasks */}
      <MySchedule filters={filters} />

      {/* Main Content */}
      <Spin spinning={refreshing} delay={200}>
        <Row gutter={[16, 16]}>
          {/* My Tasks Summary */}
          <Col xs={24} lg={12}>
            <Card title="My Assigned Tasks">
              <List
                dataSource={myTasks}
                locale={{ emptyText: <Empty description="No tasks match these filters" /> }}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.title}
                      description={`Project: ${item.project_name} | Due: ${item.due_date || 'No due date'}`}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Recent Activity Feed */}
          <Col xs={24} lg={12}>
            <Card title="Recent Activity">
              {recentActivity.length === 0 ? (
                <Empty description="No activity for these filters" />
              ) : (
                <Timeline>
                  {recentActivity.map((activity, index) => (
                    <Timeline.Item key={index}>
                      <p>{activity.text}</p>
                      <small>{activity.user_name} - {new Date(activity.created_at).toLocaleString()}</small>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Overview;
